import React, { useCallback, useEffect, useRef, useState } from "react";

import { v4 as uuidv4 } from "uuid";

import Color from "color";

import { ResizableTextarea } from "../../ui/resizable-textarea.tsx";
import FormButton from "../FormButton.tsx";

import useStyles from "../../../Hooks/useStyles.ts";

import { useFormEditorContext } from "../../../Contexts/FormEditorContext";

import { getLetterForIndex } from "../../../Utils/string";

import { cn } from "../../../lib/utils.js";

import { XIcon } from "lucide-react";

import { motion, Reorder } from "framer-motion";

const MAX_BREAK_POINT = 10;
import { questionAnimation } from "../../../Constants/animations";

function MultipleChoice() {
    const { getStyles } = useStyles();
    const saveTimeoutRef = useRef<any>(null);
    const [gridLayout, setGridLayout] = useState("");
    const {
        theme,
        editorField,
        setEditorField,
        fields,
        setFields,
        setChanged,
        device,
        changeFieldTitle,
    } = useFormEditorContext();

    useEffect(() => {
        const layout = editorField?.properties?.choices?.some(
            (choice) => choice?.label?.length > MAX_BREAK_POINT,
        )
            ? "grid-cols-1"
            : `grid-cols-${device === "desktop" ? 3 : 2}`;

        const maxHeight =
            device === "desktop" ? "max-h-[400px]" : "max-h-[300px]";
        const combinedLayout = `${layout} ${maxHeight}`;
        setGridLayout(combinedLayout);
    }, [device, editorField?.properties?.choices]);

    const updateField = (fieldId, key, subkey, value) => {
        setFields((prevFields) => {
            const updatedFields = prevFields.map((field) => {
                if (field.id === fieldId) {
                    if (subkey) {
                        return {
                            ...field,
                            [key]: {
                                ...field[key],
                                [subkey]: value,
                            },
                        };
                    } else {
                        return {
                            ...field,
                            [key]: value,
                        };
                    }
                }
                return field;
            });
            return updatedFields;
        });

        setEditorField((prev) => {
            if (subkey) {
                return {
                    ...prev,
                    [key]: {
                        ...prev[key],
                        [subkey]: value,
                    },
                };
            } else {
                return {
                    ...prev,
                    [key]: value,
                };
            }
        });

        changeFieldTitle(fieldId, value);
    };

    const scheduleSave = (updatedField) => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = setTimeout(() => {
            saveChanges(updatedField);
        }, 500); // Debounce save for 500ms
    };

    const handleAddOption = () => {
        const newOption = {
            id: uuidv4(),
            label: "",
        };
        setEditorField((prev) => ({
            ...prev,
            properties: {
                ...prev.properties,
                choices: [...(prev.properties.choices || []), newOption],
            },
        }));
    };

    const saveChanges = useCallback(
        (updatedEditorField) => {
            setChanged(true);
            const newFields = [...fields];
            const index = newFields.findIndex(
                (f) => f?.id === updatedEditorField.id,
            );

            if (updatedEditorField?.title?.trim() === "") return;

            const cleanEditorField = {
                ...updatedEditorField,
                title: updatedEditorField?.title,
                properties: {
                    ...updatedEditorField.properties,
                    description:
                        updatedEditorField.properties?.description || "",
                },
            };

            newFields[index] = Object.fromEntries(
                Object.entries(cleanEditorField).filter(
                    ([key, value]) => value != null,
                ),
            );

            setFields(newFields);
        },
        [fields, setFields, setChanged],
    );

    // Cleanup function to clear any pending timeouts
    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, []);

    const handleOnRemoveChoice = (id: string) => {
        const updatedField = {
            ...editorField,
            properties: {
                ...editorField.properties,
                choices: (editorField.properties.choices || []).filter(
                    (choice) => choice.id !== id,
                ),
            },
        };
        setEditorField(updatedField);
        scheduleSave(updatedField);
    };

    const handleOnReorder = (newChoices) => {
        setEditorField((prev) => ({
            ...prev,
            properties: { ...prev.properties, choices: newChoices },
        }));

        const index = fields.findIndex((f) => f?.id === editorField?.id);
        const updatedField = {
            ...editorField,
            properties: {
                ...editorField.properties,
                choices: newChoices,
            },
        };

        setFields((prevFields) => {
            const updatedFields = [...prevFields];
            updatedFields[index] = updatedField;
            return updatedFields;
        });
    };

    return (
        <div className="text-left flex flex-col w-full max-w-[606px] ">
            <ResizableTextarea
                placeholder="Add question title"
                value={editorField?.title || ""}
                onChange={(value) =>
                    updateField(editorField?.id, "title", null, value)
                }
                style={{
                    color: theme?.text_color,
                    fontSize: getStyles("h2", theme?.text_size, device)
                        .fontSize,
                    fontWeight: getStyles("h2", theme?.text_size, device)
                        .fontWeight,
                    fontFamily: `${theme?.font_family?.family}, ${theme?.font_family?.category}`,
                }}
            />
            <ResizableTextarea
                placeholder="Add question description (Optional)"
                value={editorField?.properties?.description || ""}
                onChange={(value) =>
                    updateField(
                        editorField?.id,
                        "properties",
                        "description",
                        value,
                    )
                }
                style={{
                    color: theme?.text_color,
                    fontSize: getStyles("h4", theme?.text_size, device)
                        .fontSize,
                    fontWeight: getStyles("h4", theme?.text_size, device)
                        .fontWeight,
                    fontFamily: `${theme?.font_family?.family}, ${theme?.font_family?.category}`,
                }}
                className="mt-2"
            />
            <motion.div
                {...questionAnimation}
                className={cn(
                    `grid overflow-y-auto gap-2 mt-4 max-h-[400px] no-scrollbar`,
                    gridLayout,
                )}
            >
                <Reorder.Group
                    className="flex flex-col gap-1 reorder-ul fill-available p-0"
                    axis="y"
                    values={editorField?.properties?.choices}
                    onReorder={handleOnReorder}
                >
                    {editorField?.properties?.choices?.map(
                        (option: any, index: number) => (
                            <Reorder.Item
                                key={option?.id}
                                value={option}
                                className="p-0"
                            >
                                <Choice
                                    id={option?.id}
                                    letter={getLetterForIndex(index)}
                                    label={option?.label}
                                    onChange={(field) => scheduleSave(field)}
                                    onKeyDown={handleAddOption}
                                    onRemove={handleOnRemoveChoice}
                                />
                            </Reorder.Item>
                        ),
                    )}
                </Reorder.Group>
            </motion.div>

            <div className="w-full mt-2">
                <button
                    style={{
                        color: theme?.answer_text_color,
                    }}
                    onClick={handleAddOption}
                    className="flex justify-start text-sm font-medium underline"
                >
                    Add Choice
                </button>
            </div>
            <FormButton className="mt-3" />
        </div>
    );
}

export default MultipleChoice;

type ChoiceProps = {
    id: string;
    letter: string;
    label: string;
    onChange: (field: any) => void;
    onKeyDown: (optionId: string, value: string) => void;
    onRemove: (id: string) => void;
};

const Choice = ({
    id,
    letter,
    label,
    onChange,
    onKeyDown,
    onRemove,
}: ChoiceProps) => {
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const { theme, setEditorField } = useFormEditorContext();
    const isAdjustingRef = useRef(false);

    const adjustHeight = useCallback(() => {
        if (!textAreaRef.current || isAdjustingRef.current) return;

        isAdjustingRef.current = true;
        requestAnimationFrame(() => {
            if (!textAreaRef.current) return;

            textAreaRef.current.style.height = "auto"; // Reset to zero first
            const scrollHeight = textAreaRef.current.scrollHeight;
            textAreaRef.current.style.height = `${scrollHeight}px`;

            isAdjustingRef.current = false;
        });
    }, []);

    // Run adjustment on mount and text changes
    useEffect(() => {
        const timeouts = [0, 50, 100].map((delay) =>
            setTimeout(adjustHeight, delay),
        );

        return () => timeouts.forEach(clearTimeout);
    }, [label, adjustHeight]);

    const adjustWidth = () => {
        if (textAreaRef.current) {
            const length = label.length;
            if (length > MAX_BREAK_POINT) {
                textAreaRef.current.style.width = `100%`;
            } else {
                textAreaRef.current.style.width = "95px";
            }
        }
    };

    useEffect(() => {
        adjustWidth(); // Adjust width whenever content changes
        adjustHeight(); // Adjust height whenever content changes
    }, [label]);

    const handleOnChange = useCallback((value) => {
        setEditorField((prev) => {
            const updatedField = {
                ...prev,
                properties: {
                    ...prev.properties,
                    choices: (prev.properties.choices || []).map((choice) =>
                        choice.id === id ? { ...choice, label: value } : choice,
                    ),
                },
            };

            onChange(updatedField);
            return updatedField;
        });

        // Maintain focus after state update
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 0);
    }, []);

    const handleOnKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter") {
            if (e.shiftKey) {
                onChange(`${e.currentTarget.value}\n`);
            } else {
                e.preventDefault();
                onKeyDown(id, e.currentTarget.value);
            }
        }
    };

    const color = new Color(theme?.answer_text_color);

    return (
        <div
            id={id}
            className="flex flex-row items-center justify-between border border-[#DCDFE4] rounded-lg p-2 max-w-full min-h-[40px] h-[40px]"
            style={{
                borderRadius: theme?.rounded_corners || 4,
                borderColor: color.hex() + "50",
                borderWidth: "1px",
                backgroundColor: color.hex() + "10", // Changing the opacity of the color
            }}
        >
            <div
                className="flex items-center justify-center bg-[#FFFFFF] rounded-lg text-center w-6 h-6"
                style={{
                    borderRadius: Number(theme?.rounded_corners || 4) - 2,
                    borderColor: color.hex() + "50",
                    borderWidth: "1px",
                    color: theme?.answer_text_color,
                    backgroundColor: "#FFFFFF",
                }}
            >
                <p
                    className="text-sm"
                    style={{
                        fontFamily: `${theme?.font_family?.family}, ${theme?.font_family?.category}`,
                        fontWeight: theme?.is_bold ? "bold" : "normal",
                    }}
                >
                    {letter}
                </p>
            </div>
            <textarea
                rows={1}
                autoFocus
                ref={textAreaRef}
                placeholder="choice"
                className={
                    "overflow-hidden flex-1 px-3 m-0 max-h-72 font-normal text-sm text-left bg-transparent border-none shadow-none duration-200 outline-none w-fit text-md focus-visible:ring-0 focus-visible:ring-offset-0 h-fit transition-height"
                }
                style={{
                    color: theme?.answer_text_color || theme?.text_color,
                    resize: "none",
                    fontWeight: theme?.is_bold ? "bold" : "normal",
                    fontFamily: `${theme?.font_family?.family}, ${theme?.font_family?.category}`,
                }}
                value={label}
                onChange={(e) => handleOnChange(e.target.value)}
                onKeyDown={handleOnKeyDown}
            />
            <button
                className="bg-transparent hover:outline-none hover:border-none"
                onClick={() => onRemove(id)}
            >
                <XIcon
                    className="w-[14px] h-[14px] mr-[4px]"
                    style={{
                        color: color.hex() + "50",
                    }}
                />
            </button>
        </div>
    );
};
